import { useEffect, type CSSProperties } from "react";
import { Checkbox, Col, Form, Input, Radio, Row } from "antd";
import type { FormInstance } from "antd";
import TextArea from "antd/lib/input/TextArea";
import CustomCard from "../../components/CustomCard";
import { AmountInput } from "../../components/form/AmountInput";
import { legacyBiblFnaConfig } from "../order-fna/legacyBiblFnaConfig";
import styles from "./FinancialForm.module.css";
import type { OrderFinancialVO } from "./orders";

type QuestionValueType = "RADIO" | "CHECKBOX" | "INPUT";

type LegacyFinancialAnalysisSectionProps = {
  data: OrderFinancialVO;
  form: FormInstance;
  onChange: (data: OrderFinancialVO) => void;
  getQuestionValue: (
    questionKey: string,
    type?: QuestionValueType,
  ) => any;
  updateQuestionValue: (
    questionKey: string,
    value: any,
    type?: QuestionValueType,
  ) => void;
  shouldShowQuestion42: () => boolean;
  monthlyDisposableIncome: number;
  yearlyDisposableIncome: number;
  monthlyDisposableIncomePremiumLimit: number;
  yearlyDisposableIncomePremiumLimit: number;
  liquidAssetsAmount: number;
  liquidAssetsPremiumLimit: number;
  formatCurrency: (value: number) => string;
  formatInteger: (value: number) => string;
};

const insetPanelStyle: CSSProperties = {
  backgroundColor: "#F5F5F7",
  padding: 16,
  borderRadius: 6,
  marginTop: 12,
};

const neutralInsetPanelStyle: CSSProperties = {
  background: "#F5F5F5",
  padding: 16,
  borderRadius: 6,
};

export function LegacyFinancialAnalysisSection({
  data,
  form,
  onChange,
  getQuestionValue,
  updateQuestionValue,
  shouldShowQuestion42,
  monthlyDisposableIncome,
  yearlyDisposableIncome,
  monthlyDisposableIncomePremiumLimit,
  yearlyDisposableIncomePremiumLimit,
  liquidAssetsAmount,
  liquidAssetsPremiumLimit,
  formatCurrency,
  formatInteger,
}: LegacyFinancialAnalysisSectionProps) {
  const investmentManagementConfig =
    legacyBiblFnaConfig.edit.investmentManagement;

  const setSingleChoiceQuestion = (questionKey: string, newValue: string) => {
    const currentFinancialAnalysis = [...(data.financialAnalysis || [])].filter(
      (question) => question.question,
    );
    const questionIndex = currentFinancialAnalysis.findIndex(
      (question) => question.question === questionKey,
    );
    const currentItems =
      questionIndex >= 0
        ? currentFinancialAnalysis[questionIndex].items || []
        : [];
    const updatedItems = currentItems.map((item) => ({
      ...item,
      value: item.key === newValue,
    }));

    if (!updatedItems.find((item) => item.key === newValue)) {
      updatedItems.push({ key: newValue, value: true });
    }

    if (questionIndex >= 0) {
      currentFinancialAnalysis[questionIndex] = {
        ...currentFinancialAnalysis[questionIndex],
        items: updatedItems,
      };
    } else {
      currentFinancialAnalysis.push({
        question: questionKey,
        type: "RADIO",
        items: updatedItems,
      });
    }

    return currentFinancialAnalysis;
  };

  useEffect(() => {
    if (shouldShowQuestion42()) {
      return;
    }

    const currentFinancialAnalysis = [...(data.financialAnalysis || [])].filter(
      (question) => question.question,
    );
    const keysToCheck = [
      "continuedContributionReason",
      "continuedContributionReasonOther",
    ];
    let hasValue = false;

    keysToCheck.forEach((key) => {
      const question = currentFinancialAnalysis.find(
        (item) => item.question === key,
      );
      if (!question) {
        return;
      }

      const hasNonEmptyValue = question.items?.some(
        (item) =>
          item.value !== false &&
          item.value !== "" &&
          item.value !== 0 &&
          item.value !== undefined,
      );
      if (hasNonEmptyValue) {
        hasValue = true;
      }
    });

    if (!hasValue) {
      return;
    }

    const nextFinancialAnalysis = currentFinancialAnalysis.map((question) => {
      if (question.question === "continuedContributionReason") {
        return {
          ...question,
          items: (question.items || []).map((item) => ({
            ...item,
            value: false,
          })),
        };
      }

      if (question.question === "continuedContributionReasonOther") {
        return {
          ...question,
          items: [{ key: "A", value: "" }],
        };
      }

      return question;
    });

    form.setFieldValue("continuedContributionReason", undefined);
    form.setFieldValue("continuedContributionReasonOther", undefined);
    onChange({
      ...data,
      financialAnalysis: nextFinancialAnalysis,
    });
  }, [data, form, onChange, shouldShowQuestion42]);

  return (
    <CustomCard
      title="三、財務需要分析"
      className={styles.sectionCard}
      bodyStyle={{ paddingTop: 0 }}
    >
      <div style={{ marginBottom: 24, borderRadius: 6 }}>
        <Form.Item
          label="1. 閣下購買保險產品的目標為何？ (勾選一項或多項)"
          name="insurancePurchaseObjectives"
          rules={[{ required: true, message: "請至少選擇一項" }]}
        >
          <Checkbox.Group
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
            value={
              getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") || []
            }
            onChange={(checkedValues) => {
              const currentFinancialAnalysis = [
                ...(data.financialAnalysis || []),
              ];
              const objectivesIndex = currentFinancialAnalysis.findIndex(
                (question) => question.question === "insurancePurchaseObjectives",
              );
              const keys = (checkedValues as string[]) || [];
              const objectivesItems = keys.map((key) => ({
                key,
                value: true,
              }));

              if (objectivesIndex >= 0) {
                currentFinancialAnalysis[objectivesIndex] = {
                  ...currentFinancialAnalysis[objectivesIndex],
                  items: objectivesItems,
                };
              } else {
                currentFinancialAnalysis.push({
                  question: "insurancePurchaseObjectives",
                  type: "CHECKBOX",
                  items: objectivesItems,
                });
              }

              const currentValues =
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                [];
              const newlyChecked = keys.filter(
                (value) => !currentValues.includes(value),
              );

              newlyChecked.forEach((option) => {
                let inputQuestionKey = "";
                if (option === "A") {
                  inputQuestionKey = "additionalLifeCoverageAmount";
                } else if (option === "B") {
                  inputQuestionKey = "additionalCriticalIllnessCoverageAmount";
                } else if (option === "C" || option === "D") {
                  inputQuestionKey = "additionalSavingsTargetReturnAmount";
                } else if (option === "E") {
                  inputQuestionKey = "additionalInvestmentTargetReturnAmount";
                }

                if (inputQuestionKey) {
                  const existingIndex = currentFinancialAnalysis.findIndex(
                    (question) => question.question === inputQuestionKey,
                  );
                  if (existingIndex < 0) {
                    currentFinancialAnalysis.push({
                      question: inputQuestionKey,
                      type: "INPUT",
                      items: [{ key: "A", value: 0 }],
                    });
                  }
                }
              });

              const newlyUnchecked = currentValues.filter(
                (value) => !keys.includes(value),
              );

              newlyUnchecked.forEach((option) => {
                const fieldsToClear: string[] = [];
                if (option === "A") {
                  fieldsToClear.push("additionalLifeCoverageAmount");
                } else if (option === "B") {
                  fieldsToClear.push("additionalCriticalIllnessCoverageAmount");
                } else if (option === "C" || option === "D") {
                  const otherOption = option === "C" ? "D" : "C";
                  if (!keys.includes(otherOption)) {
                    fieldsToClear.push(
                      "additionalSavingsTargetReturnPeriod",
                      "additionalSavingsTargetReturnAmount",
                    );
                  }
                } else if (option === "E") {
                  fieldsToClear.push(
                    "additionalInvestmentTargetReturnPeriod",
                    "additionalInvestmentTargetReturnAmount",
                  );
                } else if (option === "F") {
                  fieldsToClear.push("insurancePurchaseOtherObjectives");
                }

                fieldsToClear.forEach((fieldKey) => {
                  const fieldIndex = currentFinancialAnalysis.findIndex(
                    (question) => question.question === fieldKey,
                  );
                  if (fieldIndex >= 0) {
                    currentFinancialAnalysis[fieldIndex] = {
                      ...currentFinancialAnalysis[fieldIndex],
                      items: [],
                    };
                  }
                  form.setFieldValue(fieldKey, undefined);
                });
              });

              onChange({
                ...data,
                financialAnalysis: currentFinancialAnalysis,
              });
              form.setFieldValue("insurancePurchaseObjectives", checkedValues);
            }}
          >
            <div>
              <Checkbox value="A" style={{ marginBottom: 8 }}>
                A. 為應付不時之需提供財務保障(如身故、意外、殘疾)
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("A") && (
                <div style={insetPanelStyle}>
                  <Form.Item
                    label="額外人壽保障額 (HK$/港幣)"
                    name="additionalLifeCoverageAmount"
                    rules={[
                      {
                        required: true,
                        message: "請輸入額外人壽保障額",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <AmountInput
                      style={{ width: "100%" }}
                      placeholder="請輸入"
                      value={
                        getQuestionValue(
                          "additionalLifeCoverageAmount",
                          "INPUT",
                        ) ?? 0
                      }
                      onChange={(value) => {
                        updateQuestionValue(
                          "additionalLifeCoverageAmount",
                          value ?? 0,
                          "INPUT",
                        );
                      }}
                    />
                  </Form.Item>
                </div>
              )}
            </div>

            <div>
              <Checkbox value="B" style={{ marginBottom: 8 }}>
                B. 為應付醫療保健需要(如危疾、住院)
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("B") && (
                <div style={insetPanelStyle}>
                  <Form.Item
                    label="額外危疾保障額 (HK$/港幣)"
                    name="additionalCriticalIllnessCoverageAmount"
                    rules={[
                      {
                        required: true,
                        message: "請輸入額外危疾保障額",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <AmountInput
                      style={{ width: "100%" }}
                      placeholder="請輸入"
                      value={
                        getQuestionValue(
                          "additionalCriticalIllnessCoverageAmount",
                          "INPUT",
                        ) ?? 0
                      }
                      onChange={(value) => {
                        updateQuestionValue(
                          "additionalCriticalIllnessCoverageAmount",
                          value ?? 0,
                          "INPUT",
                        );
                      }}
                    />
                  </Form.Item>
                </div>
              )}
            </div>

            <div>
              <Checkbox value="C" style={{ marginBottom: 8 }}>
                C. 為未來提供定期的收入(如退休收入)
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("C") && (
                <div style={insetPanelStyle}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="年齡/年期"
                        name="additionalSavingsTargetReturnPeriod"
                        rules={[
                          {
                            required: true,
                            message: "請輸入年齡/年期",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="請輸入"
                          type="number"
                          value={getQuestionValue(
                            "additionalSavingsTargetReturnPeriod",
                            "INPUT",
                          )}
                          onChange={(event) =>
                            updateQuestionValue(
                              "additionalSavingsTargetReturnPeriod",
                              event.target.value,
                              "INPUT",
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="額外回報金額(HK$/港幣)"
                        name="additionalSavingsTargetReturnAmount"
                        rules={[
                          {
                            required: true,
                            message: "請輸入額外回報金額",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={
                            getQuestionValue(
                              "additionalSavingsTargetReturnAmount",
                              "INPUT",
                            ) ?? 0
                          }
                          onChange={(value) => {
                            updateQuestionValue(
                              "additionalSavingsTargetReturnAmount",
                              value ?? 0,
                              "INPUT",
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            <div>
              <Checkbox value="D" style={{ marginBottom: 8 }}>
                D. 為未來需要做儲蓄(如子女教育、退休)
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("D") && (
                <div style={insetPanelStyle}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="年齡/年期"
                        name="additionalSavingsTargetReturnPeriod"
                        rules={[
                          {
                            required: true,
                            message: "請輸入年齡/年期",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="請輸入"
                          value={getQuestionValue(
                            "additionalSavingsTargetReturnPeriod",
                            "INPUT",
                          )}
                          onChange={(event) =>
                            updateQuestionValue(
                              "additionalSavingsTargetReturnPeriod",
                              event.target.value,
                              "INPUT",
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="額外回報金額(HK$/港幣)"
                        name="additionalSavingsTargetReturnAmount"
                        rules={[
                          {
                            required: true,
                            message: "請輸入額外回報金額",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={
                            getQuestionValue(
                              "additionalSavingsTargetReturnAmount",
                              "INPUT",
                            ) ?? 0
                          }
                          onChange={(value) => {
                            updateQuestionValue(
                              "additionalSavingsTargetReturnAmount",
                              value ?? 0,
                              "INPUT",
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            <div>
              <Checkbox value="E" style={{ marginBottom: 8 }}>
                E. 投資
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("E") && (
                <div style={insetPanelStyle}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="年齡/年期"
                        name="additionalInvestmentTargetReturnPeriod"
                        rules={[
                          {
                            required: true,
                            message: "請輸入年齡/年期",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="請輸入"
                          type="number"
                          value={
                            getQuestionValue(
                              "additionalInvestmentTargetReturnPeriod",
                              "INPUT",
                            ) ?? 0
                          }
                          onChange={(event) =>
                            updateQuestionValue(
                              "additionalInvestmentTargetReturnPeriod",
                              event.target.value ?? 0,
                              "INPUT",
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="額外回報金額(HK$/港幣)"
                        name="additionalInvestmentTargetReturnAmount"
                        rules={[
                          {
                            required: true,
                            message: "請輸入額外回報金額",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={
                            getQuestionValue(
                              "additionalInvestmentTargetReturnAmount",
                              "INPUT",
                            ) ?? 0
                          }
                          onChange={(value) => {
                            updateQuestionValue(
                              "additionalInvestmentTargetReturnAmount",
                              value ?? 0,
                              "INPUT",
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            <div>
              <Checkbox value="F" style={{ marginBottom: 8 }}>
                F. 其他
              </Checkbox>
              {(
                getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") ||
                []
              ).includes("F") && (
                <div style={insetPanelStyle}>
                  <Form.Item
                    label="請詳述"
                    name="insurancePurchaseOtherObjectives"
                    rules={[
                      {
                        required: true,
                        message: "請輸入詳細內容",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      placeholder="請輸入詳細內容"
                      value={getQuestionValue(
                        "insurancePurchaseOtherObjectives",
                        "INPUT",
                      )}
                      onChange={(event) =>
                        updateQuestionValue(
                          "insurancePurchaseOtherObjectives",
                          event.target.value,
                          "INPUT",
                        )
                      }
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          </Checkbox.Group>

          {(
            getQuestionValue("insurancePurchaseObjectives", "CHECKBOX") || []
          ).includes("E") && (
            <Form.Item
              style={{ marginTop: "48px" }}
              label={investmentManagementConfig.label}
              name="investmentOptionManagementPreference"
              rules={[
                {
                  required: true,
                  message: investmentManagementConfig.validationMessage,
                },
              ]}
            >
              <Radio.Group
                style={{ width: "100%" }}
                value={getQuestionValue(
                  "investmentOptionManagementPreference",
                  "RADIO",
                )}
                onChange={(event) =>
                  updateQuestionValue(
                    "investmentOptionManagementPreference",
                    event.target.value,
                    "RADIO",
                  )
                }
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {investmentManagementConfig.options.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      style={{
                        whiteSpace: "normal",
                        alignItems: "flex-start",
                      }}
                    >
                      {option.label}
                    </Radio>
                  ))}
                </div>
              </Radio.Group>
            </Form.Item>
          )}

          {getQuestionValue("investmentOptionManagementPreference", "RADIO") ===
            investmentManagementConfig.nestedVisibleValue && (
              <div
                style={{
                  ...neutralInsetPanelStyle,
                  marginLeft: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <Form.Item
                  label={investmentManagementConfig.withdrawalLabel}
                  name="investmentWithdrawalStartTime"
                  rules={[{ required: true }]}
                >
                  <Radio.Group
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                    value={getQuestionValue(
                      "investmentWithdrawalStartTime",
                      "RADIO",
                    )}
                    onChange={(event) =>
                      updateQuestionValue(
                        "investmentWithdrawalStartTime",
                        event.target.value,
                        "RADIO",
                      )
                    }
                  >
                    {legacyBiblFnaConfig.options.investmentWithdrawalOptions.map(
                      (option) => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ),
                    )}
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label={investmentManagementConfig.longTermGoalLabel}
                  name="investmentLongTermGoal"
                  rules={[{ required: true }]}
                >
                  <Radio.Group
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                    value={getQuestionValue("investmentLongTermGoal", "RADIO")}
                    onChange={(event) =>
                      updateQuestionValue(
                        "investmentLongTermGoal",
                        event.target.value,
                        "RADIO",
                      )
                    }
                  >
                    {legacyBiblFnaConfig.options.investmentGoalOptions.map(
                      (option) => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ),
                    )}
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label={investmentManagementConfig.expectationLabel}
                  name="investmentExpectationIfMarketPoor"
                  rules={[{ required: true }]}
                >
                  <Radio.Group
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                    value={getQuestionValue(
                      "investmentExpectationIfMarketPoor",
                      "RADIO",
                    )}
                    onChange={(event) =>
                      updateQuestionValue(
                        "investmentExpectationIfMarketPoor",
                        event.target.value,
                        "RADIO",
                      )
                    }
                  >
                    {legacyBiblFnaConfig.options.investmentExpectationOptions.map(
                      (option) => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ),
                    )}
                  </Radio.Group>
                </Form.Item>
              </div>
            )}
        </Form.Item>

        <Form.Item
          label="2. 閣下投購保單的目標得益/保障年期/實現目標金額的預期時間為多久？（勾選一項）。"
          name="policyTargetExpectedTime"
          rules={[{ required: true }]}
        >
          <Radio.Group
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
            value={getQuestionValue("policyTargetExpectedTime", "RADIO")}
            onChange={(event) =>
              updateQuestionValue(
                "policyTargetExpectedTime",
                event.target.value,
                "RADIO",
              )
            }
          >
            <Radio value="A">少於1年</Radio>
            <Radio value="B">1-5年</Radio>
            <Radio value="C">6-10年</Radio>
            <Radio value="D">11-15年</Radio>
            <Radio value="E">16-20年</Radio>
            <Radio value="F">超過20年</Radio>
            <Radio value="G">終身</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="3.1 閣下支付保費的能力：在整個保單期內，閣下能夠及願意繳付的保費（包括閣下現有的其他保單）佔透過所有收入來源（包括流動資產收入）獲得的每月可動用收入（即經扣除開支）的比率為？（勾選一項）。"
          name="premiumToDisposableIncomeRatio"
          rules={[{ required: true }]}
        >
          <Radio.Group
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
            value={getQuestionValue("premiumToDisposableIncomeRatio", "RADIO")}
            onChange={(event) =>
              updateQuestionValue(
                "premiumToDisposableIncomeRatio",
                event.target.value,
                "RADIO",
              )
            }
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <Radio value="A">A. 少於10%</Radio>
              {getQuestionValue("premiumToDisposableIncomeRatio", "RADIO") ===
                "A" && (
                <div>
                  <Input
                    placeholder="請輸入具體比例"
                    type="number"
                    inputMode="numeric"
                    step="1"
                    min={0}
                    max={9}
                    suffix="%"
                    style={{ width: "100%" }}
                    value={getQuestionValue(
                      "premiumToDisposableIncomeRatio_A_rate",
                      "INPUT",
                    )}
                    onChange={(event) => {
                      const value = event.target.valueAsNumber;
                      const rounded = Number.isNaN(value)
                        ? undefined
                        : Math.round(value);
                      updateQuestionValue(
                        "premiumToDisposableIncomeRatio_A_rate",
                        rounded,
                        "INPUT",
                      );
                    }}
                    onBlur={(event) => {
                      const value = event.target.valueAsNumber;
                      if (!Number.isNaN(value)) {
                        const rounded = Math.min(
                          9,
                          Math.max(0, Math.round(value)),
                        );
                        updateQuestionValue(
                          "premiumToDisposableIncomeRatio_A_rate",
                          rounded,
                          "INPUT",
                        );
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <Radio value="B">B. 10%-20%</Radio>
            </div>
            <Radio value="C">C. 21%-30%</Radio>
            <Radio value="D">D. 31%-40%</Radio>
            <Radio value="E">E. 41%-50%</Radio>
            <Radio value="F">F. 超過50%</Radio>
            {getQuestionValue("premiumToDisposableIncomeRatio", "RADIO") ===
              "F" && (
              <div style={{ gridColumn: "1 / -1", marginLeft: "50%" }}>
                <Input
                  placeholder="請輸入具體比例"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min={51}
                  max={100}
                  suffix="%"
                  style={{ width: "100%", marginTop: 8 }}
                  value={getQuestionValue(
                    "premiumToDisposableIncomeRatio_F_rate",
                    "INPUT",
                  )}
                  onChange={(event) => {
                    const value = event.target.valueAsNumber;
                    const rounded = Number.isNaN(value)
                      ? undefined
                      : Math.round(value);
                    updateQuestionValue(
                      "premiumToDisposableIncomeRatio_F_rate",
                      rounded,
                      "INPUT",
                    );
                  }}
                  onBlur={(event) => {
                    const value = event.target.valueAsNumber;
                    if (!Number.isNaN(value)) {
                      const rounded = Math.min(
                        100,
                        Math.max(51, Math.round(value)),
                      );
                      updateQuestionValue(
                        "premiumToDisposableIncomeRatio_F_rate",
                        rounded,
                        "INPUT",
                      );
                    }
                  }}
                />
              </div>
            )}
            <Radio value="G">G. 不適用</Radio>
          </Radio.Group>
          {getQuestionValue("premiumToDisposableIncomeRatio", "RADIO") ===
            "G" && (
            <Input
              placeholder="請輸入原因"
              style={{ marginTop: 8 }}
              value={getQuestionValue(
                "premiumToIncomeRatioNotApplicableReason",
                "INPUT",
              )}
              onChange={(event) =>
                updateQuestionValue(
                  "premiumToIncomeRatioNotApplicableReason",
                  event.target.value,
                  "INPUT",
                )
              }
            />
          )}
        </Form.Item>

        <Form.Item
          label="3.2 閣下支付保費的能力：在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔淨流動資產比率為？（勾選一項）。"
          name="premiumToLiquidAssetsRatio"
          rules={[{ required: true }]}
        >
          <Radio.Group
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
            value={getQuestionValue("premiumToLiquidAssetsRatio", "RADIO")}
            onChange={(event) =>
              updateQuestionValue(
                "premiumToLiquidAssetsRatio",
                event.target.value,
                "RADIO",
              )
            }
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <Radio value="A">A. 少於10%</Radio>
              {getQuestionValue("premiumToLiquidAssetsRatio", "RADIO") ===
                "A" && (
                <div>
                  <Input
                    placeholder="請輸入具體比例"
                    type="number"
                    inputMode="numeric"
                    step="1"
                    min={0}
                    max={9}
                    suffix="%"
                    style={{ width: "100%" }}
                    value={getQuestionValue(
                      "premiumToLiquidAssetsRatio_A_rate",
                      "INPUT",
                    )}
                    onChange={(event) => {
                      const value = event.target.valueAsNumber;
                      const rounded = Number.isNaN(value)
                        ? undefined
                        : Math.round(value);
                      updateQuestionValue(
                        "premiumToLiquidAssetsRatio_A_rate",
                        rounded,
                        "INPUT",
                      );
                    }}
                    onBlur={(event) => {
                      const value = event.target.valueAsNumber;
                      if (!Number.isNaN(value)) {
                        const rounded = Math.min(
                          9,
                          Math.max(0, Math.round(value)),
                        );
                        updateQuestionValue(
                          "premiumToLiquidAssetsRatio_A_rate",
                          rounded,
                          "INPUT",
                        );
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <Radio value="B">B. 10%-20%</Radio>
            </div>
            <Radio value="C">C. 21%-30%</Radio>
            <Radio value="D">D. 31%-40%</Radio>
            <Radio value="E">E. 41%-50%</Radio>
            <Radio value="F">F. 超過50%</Radio>
            {getQuestionValue("premiumToLiquidAssetsRatio", "RADIO") ===
              "F" && (
              <div style={{ gridColumn: "1 / -1", marginLeft: "50%" }}>
                <Input
                  placeholder="請輸入具體比例"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min={51}
                  max={100}
                  suffix="%"
                  style={{ width: "100%", marginTop: 8 }}
                  value={getQuestionValue(
                    "premiumToLiquidAssetsRatio_F_rate",
                    "INPUT",
                  )}
                  onChange={(event) => {
                    const value = event.target.valueAsNumber;
                    const rounded = Number.isNaN(value)
                      ? undefined
                      : Math.round(value);
                    updateQuestionValue(
                      "premiumToLiquidAssetsRatio_F_rate",
                      rounded,
                      "INPUT",
                    );
                  }}
                  onBlur={(event) => {
                    const value = event.target.valueAsNumber;
                    if (!Number.isNaN(value)) {
                      const rounded = Math.min(
                        100,
                        Math.max(51, Math.round(value)),
                      );
                      updateQuestionValue(
                        "premiumToLiquidAssetsRatio_F_rate",
                        rounded,
                        "INPUT",
                      );
                    }
                  }}
                />
              </div>
            )}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="3.3 閣下交付保費的能力： 阁下打算以何种方式支付保费？"
          name="intendedPremiumPaymentMethods"
          rules={[{ required: true, message: "請至少選擇一項" }]}
        >
          <Checkbox.Group
            style={{ display: "flex", gap: 24 }}
            value={
              getQuestionValue("intendedPremiumPaymentMethods", "CHECKBOX") ||
              []
            }
            onChange={(checkedValues) => {
              updateQuestionValue(
                "intendedPremiumPaymentMethods",
                checkedValues,
                "CHECKBOX",
              );
              form.setFieldValue(
                "intendedPremiumPaymentMethods",
                checkedValues,
              );
            }}
          >
            <Checkbox value="A">
              A. 每月所有收入來源即經扣除開支的可動用收入
            </Checkbox>
            <Checkbox value="B">B. 流動資產</Checkbox>
          </Checkbox.Group>
        </Form.Item>

        {((getQuestionValue("intendedPremiumPaymentMethods", "CHECKBOX") ||
          []) as string[]).includes("A") ||
        ((getQuestionValue("intendedPremiumPaymentMethods", "CHECKBOX") ||
          []) as string[]).includes("B") ? (
          <div style={neutralInsetPanelStyle}>
            {((getQuestionValue("intendedPremiumPaymentMethods", "CHECKBOX") ||
              []) as string[]).includes("A") && (
              <>
                <div style={{ marginBottom: 8 }}>
                  a. 每月可動用收入: {formatInteger(monthlyDisposableIncome)}{" "}
                  HKD/月 {formatInteger(yearlyDisposableIncome)} HKD/年
                </div>
                <div style={{ marginBottom: 8 }}>
                  a1. 每月可動用收入願意繳付保費的最高金額:{" "}
                  {formatCurrency(monthlyDisposableIncomePremiumLimit)} HKD/月{" "}
                  {formatCurrency(yearlyDisposableIncomePremiumLimit)} HKD/年
                </div>
              </>
            )}
            {((getQuestionValue("intendedPremiumPaymentMethods", "CHECKBOX") ||
              []) as string[]).includes("B") && (
              <>
                <div style={{ marginBottom: 8 }}>
                  b. 流動資產: {formatInteger(liquidAssetsAmount)} HKD
                </div>
                <div>
                  b1. 流動資產願意繳付保費最高金額:{" "}
                  {formatCurrency(liquidAssetsPremiumLimit)} HKD
                </div>
              </>
            )}
          </div>
        ) : null}

        <div style={{ color: "red", marginTop: 8, fontSize: 14 }}>
          *請注意，閣下的繳費承擔能力，需要涵蓋正在申請和即將申請的保單總保費
        </div>

        <Form.Item
          label="4.1 阁下缴付保费的意愿：閣下能夠及願意為保單支付保費的年期為？（勾選一項）"
          style={{ marginTop: 24 }}
          name="willingPremiumPaymentPeriodRange"
          rules={[{ required: true }]}
        >
          <Radio.Group
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
            value={getQuestionValue("willingPremiumPaymentPeriodRange", "RADIO")}
            onChange={(event) => {
              const newValue = event.target.value;
              const currentFinancialAnalysis = setSingleChoiceQuestion(
                "willingPremiumPaymentPeriodRange",
                newValue,
              );

              if (newValue !== "G") {
                const limitIndex = currentFinancialAnalysis.findIndex(
                  (question) => question.question === "singlePremiumPaymentLimit",
                );
                if (limitIndex >= 0) {
                  currentFinancialAnalysis[limitIndex] = {
                    ...currentFinancialAnalysis[limitIndex],
                    items: [{ key: "A", value: 0 }],
                  };
                }
                form.setFieldValue("singlePremiumPaymentLimit", 0);
              }

              onChange({
                ...data,
                financialAnalysis: currentFinancialAnalysis,
              });
            }}
          >
            <Radio value="A">2-5年</Radio>
            <Radio value="B">6-10年</Radio>
            <Radio value="C">11-15年</Radio>
            <Radio value="D">16-20年</Radio>
            <Radio value="E">超過20年</Radio>
            <Radio value="F">終身</Radio>
            <Radio value="G">一次性付款不超過</Radio>
          </Radio.Group>

          {getQuestionValue("willingPremiumPaymentPeriodRange", "RADIO") ===
            "G" && (
            <AmountInput
              placeholder="請輸入金額"
              addonAfter="HKD"
              style={{ marginTop: 8, width: "100%" }}
              value={getQuestionValue("singlePremiumPaymentLimit", "INPUT") ?? 0}
              onChange={(value) => {
                updateQuestionValue(
                  "singlePremiumPaymentLimit",
                  value ?? 0,
                  "INPUT",
                );
              }}
            />
          )}
        </Form.Item>

        {shouldShowQuestion42() && (
          <Form.Item label="4.2 如供款年期超過一般退休年歲或在保險建議項目上的期望退休年歲，請說明可以持續供款的原因。（勾選一項）">
            <Radio.Group
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
              value={getQuestionValue("continuedContributionReason", "RADIO")}
              onChange={(event) => {
                const newValue = event.target.value;
                const currentFinancialAnalysis = setSingleChoiceQuestion(
                  "continuedContributionReason",
                  newValue,
                );

                if (newValue !== "B") {
                  const otherIndex = currentFinancialAnalysis.findIndex(
                    (question) =>
                      question.question === "continuedContributionReasonOther",
                  );
                  if (otherIndex >= 0) {
                    currentFinancialAnalysis[otherIndex] = {
                      ...currentFinancialAnalysis[otherIndex],
                      items: [{ key: "A", value: "" }],
                    };
                  }
                  form.setFieldValue(
                    "continuedContributionReasonOther",
                    undefined,
                  );
                }

                onChange({
                  ...data,
                  financialAnalysis: currentFinancialAnalysis,
                });
              }}
            >
              <Radio value="A">
                保費會根據退休時的實際需要，由退休儲蓄金支付。
              </Radio>
              <Radio value="B">其他</Radio>
            </Radio.Group>
            {getQuestionValue("continuedContributionReason", "RADIO") ===
              "B" && (
              <Input
                placeholder="請輸入原因"
                style={{ marginTop: 8 }}
                value={getQuestionValue(
                  "continuedContributionReasonOther",
                  "INPUT",
                )}
                onChange={(event) =>
                  updateQuestionValue(
                    "continuedContributionReasonOther",
                    event.target.value,
                    "INPUT",
                  )
                }
              />
            )}
          </Form.Item>
        )}
      </div>
    </CustomCard>
  );
}
